import { TranslationKey } from './translation/TranslationKey';
import { cloneDeep } from '@apollo/client/utilities';
import { CompanyEntity, forCreate, forDelete, forUpdate } from './common/entity';
import { t } from './translation/Translator';
import { CustomFieldEntitySubType, CustomFieldValue, WithCustomFields } from './custom_field/customField';
import { applyMixins } from '../util/mixins';
import { User } from './user';
import { Container } from './container';
import { SystemStockLocation } from './stock_location/stockLocation';
import { KitTemplate } from './kit_template/kitTemplate';
import { Order } from './order';
import { Kit } from './kit/kit';
import { toMap } from '../util/map.util';

export enum ProductTransactionType {
  inbound = 'inbound',
  outbound = 'outbound',
  move = 'move',
  replenish = 'replenish',
}

export const productTransactionTypeToCustomFieldEntitySubType = (type: ProductTransactionType) => {
  switch (type) {
    case ProductTransactionType.inbound:
      return CustomFieldEntitySubType.inbound;
    case ProductTransactionType.move:
      return CustomFieldEntitySubType.move;
    case ProductTransactionType.outbound:
      return CustomFieldEntitySubType.outbound;
  }
};

export enum ProductTransactionParentType {
  order = 'order',
  task = 'task',
  quickAction = 'quickAction',
  transaction = 'transaction', // For rollback
  upload = 'upload',

  break = 'break',
  bundle = 'bundle',
}

export function productTransactionParentTypeToString(type?: ProductTransactionParentType) {
  switch (type) {
    case ProductTransactionParentType.upload:
      return 'Upload';
    case ProductTransactionParentType.order:
      return 'Order';
    case ProductTransactionParentType.quickAction:
      return 'Quick Action';
    case ProductTransactionParentType.task:
      return 'Task';
    case ProductTransactionParentType.transaction:
      return 'Rollback';
    case ProductTransactionParentType.break:
      return t(TranslationKey.handlingUnitBreak).singular.label;
    case ProductTransactionParentType.bundle:
      return t(TranslationKey.handlingUnitBundle).singular.label;
  }
  return '';
}

export enum ProductTransactionProcessType {
  product = 'product',
  asset = 'asset',
}

export class KitSpecifiers {
  templateId?: string;
  complete?: boolean;
  fromKitId?: string;
  toKitId?: string;

  constructor(fromKitId?: string, toKitId?: string, templateId?: string, complete?: boolean) {
    this.fromKitId = fromKitId;
    this.toKitId = toKitId;
    this.templateId = templateId;
    this.complete = complete;
  }
}

export class ProductTransactionSpecifiers {
  id?: string;
  toStockLocationId?: string;
  fromStockLocationId?: string;
  toProductId?: string;
  fromProductId?: string;
  lotId?: string;
  containerId?: string;
  pmdId?: string;
  serialNbr?: string;
  lpn?: string;
  toBinId?: string;
  fromBinId?: string;
  fromContainerId?: string;
  toContainerId?: string;
  countryOfOrigin?: string;
  quantity!: number;
  processedQuantity?: number;
  fromCustomFields: Map<string, CustomFieldValue> = new Map();
  toCustomFields: Map<string, CustomFieldValue> = new Map();

  containerComplete: boolean = true;
  kit?: KitSpecifiers = undefined;

  handlingUnitId?: string;

  constructor(obj: any) {
    Object.assign(this, obj);
    this.fromCustomFields = ProductTransactionSpecifiers.toCustomFieldsMap(obj, 'fromCustomFields');
    this.toCustomFields = ProductTransactionSpecifiers.toCustomFieldsMap(obj, 'toCustomFields');
  }

  static toCustomFieldsMap(obj: any, key: keyof ProductTransactionSpecifiers): Map<string, CustomFieldValue> {
    if (!obj[key]) return new Map();

    if (Array.isArray(obj[key])) {
      return toMap(obj[key], 'id');
    } else if (obj[key] instanceof Map) {
      return new Map(obj[key]);
    } else {
      return new Map();
    }
  }

  withToCustomField(value: CustomFieldValue) {
    this.toCustomFields.set(value.id, {
      id: value.id,
      name: value.name,
      generated: value.generated,
      index: value.index,
      value: value.value,
      mandatory: value.mandatory,
      entityType: value.entityType,
      hidden: value.hidden,
      onCreate: value.onCreate,
      type: value.type,
    });
    return cloneDeep(this);
  }

  withFromCustomField(value: CustomFieldValue) {
    this.fromCustomFields.set(value.id, {
      id: value.id,
      name: value.name,
      generated: value.generated,
      index: value.index,
      value: value.value,
      mandatory: value.mandatory,
      entityType: value.entityType,
      hidden: value.hidden,
      onCreate: value.onCreate,
      type: value.type,
    });
    return cloneDeep(this);
  }
}

class ProductTransactionMetaData {
  toQuantity?: string;
  fromQuantity?: string;
  assignedUser?: User;
  order?: Order;
  fromKit?: Kit;
  toKit?: Kit;
  kitTemplate?: KitTemplate;

  fromContainer?: Container;
  toContainer?: Container;

  user?: User;

  processHistory: { statusId: string; processedAt: Date; processedBy: string; user: User }[] = [];
}

export class ProductTransaction extends CompanyEntity {
  flowId!: string;

  statusId!: string;
  type!: ProductTransactionType;

  product!: ProductTransactionSpecifiers;

  parentId?: string;
  parentType?: ProductTransactionParentType;

  processedAt?: string;
  processedBy?: string;
  pickedAt?: string;
  pickedBy?: string;
  metadata?: ProductTransactionMetaData = new ProductTransactionMetaData();

  containerTransactionId?: string;
  kitTransactionId?: string;

  processType: ProductTransactionProcessType = ProductTransactionProcessType.product;

  constructor(obj: any) {
    if (!obj.companyId) return;
    super(obj.companyId);
    Object.assign(this, cloneDeep(obj));
    this.customFields = WithCustomFields.toCustomFieldsMap(obj);
    this.product.processedQuantity = this.product.quantity;
    this.product = new ProductTransactionSpecifiers(this.product);
  }

  static hasPicking(transaction: ProductTransaction) {
    return transaction.type === ProductTransactionType.outbound || transaction.type === ProductTransactionType.move;
  }

  forCreate(): CreateProductTransactionInput {
    return {
      ...this,
      customFields: [...(this.customFields?.values() || [])],
      product: {
        ...this.product,
        fromCustomFields: [...(this.product.fromCustomFields?.values() || [])],
        toCustomFields: [...(this.product.toCustomFields?.values() || [])],
      },
    };
  }

  forUpdate(): UpdateProductTransactionInput {
    const pt = UpdateProductTransactionInput.from(this, UpdateProductTransactionInput);
    delete pt.metadata;
    return {
      ...pt,
      customFields: [...(this.customFields?.values() || [])],
      product: {
        ...pt.product,
        fromCustomFields: [...(this.product.fromCustomFields?.values() || [])],
        toCustomFields: [...(this.product.toCustomFields?.values() || [])],
      },
    } as any as UpdateProductTransactionInput; // TODO: How is this done in order.ts?
  }

  forDelete(): DeleteProductTransactionInput {
    return DeleteProductTransactionInput.from(this, DeleteProductTransactionInput);
  }

  validate(fields: (keyof ProductTransaction)[]) {
    return this.validateEntity(fields, field => {
      return null;
    });
  }

  withType(type: ProductTransactionType) {
    this.type = type;

    if (type === ProductTransactionType.inbound) {
      this.product.fromStockLocationId = undefined;
      this.product.fromBinId = undefined;
      this.product.fromContainerId = undefined;
    } else if (type === ProductTransactionType.outbound) {
      this.product.toStockLocationId = undefined;
      this.product.toBinId = undefined;
      this.product.toContainerId = undefined;
    }
    return cloneDeep(this);
  }

  withContainerComplete(containerComplete: boolean) {
    this.product.containerComplete = containerComplete;
    return cloneDeep(this);
  }

  withToStockLocationId(stockLocationId?: string | null) {
    this.product.toStockLocationId = stockLocationId || undefined;
    this.product.toBinId = undefined;
    this.product.toContainerId = undefined;
    return cloneDeep(this);
  }

  withFromStockLocationId(stockLocationId?: string | null) {
    this.product.fromStockLocationId = stockLocationId || undefined;
    this.product.fromBinId = undefined;
    this.product.kit = undefined;
    return cloneDeep(this);
  }

  /// You would want to resetContainer if the destination container is virtual or if the transaction is a child of a container transaction
  withToBinId(binId?: string, resetContainer: boolean = false) {
    this.product.toBinId = binId;
    if (!resetContainer) this.product.toContainerId = undefined;
    this.product.kit = undefined;
    return cloneDeep(this);
  }

  withFromBinId(binId?: string) {
    this.product.fromBinId = binId;
    this.product.fromContainerId = undefined;
    return cloneDeep(this);
  }

  withToContainer(container?: Container) {
    if (container && container.stockLocationId !== SystemStockLocation.id) {
      this.product.toStockLocationId = container.stockLocationId;
      this.product.toBinId = container.binId;
      this.product.kit = undefined;
    }

    return this.withToContainerId(container?.id);
  }

  withToContainerId(containerId?: string) {
    this.product.toContainerId = containerId;
    return cloneDeep(this);
  }

  withFromContainerId(containerId?: string) {
    this.product.fromContainerId = containerId;
    if (this.product.toContainerId === containerId) this.product.toContainerId = undefined;
    return cloneDeep(this);
  }

  withSerial(serial?: string) {
    this.product.serialNbr = serial;
    return cloneDeep(this);
  }

  withLPN(lpn?: string) {
    this.product.lpn = lpn;
    return cloneDeep(this);
  }

  withLotId(lotId?: string) {
    this.product.lotId = lotId;
    return cloneDeep(this);
  }

  withQuantity(quantity: number) {
    this.product.quantity = quantity;
    return cloneDeep(this);
  }

  withProcessedQuantity(quantity: number) {
    this.product.processedQuantity = quantity;
    return cloneDeep(this);
  }

  withCustomField(customFieldValue: CustomFieldValue) {
    this.customFields.set(customFieldValue.id, customFieldValue);
    return cloneDeep(this);
  }

  withToCustomField(value: CustomFieldValue | null, id?: string) {
    if (!value && !id) throw new Error('Either value or id must be provided');
    if (!value) return this.removeToCustomField(id!);
    this.product.toCustomFields.set(value.id, {
      id: value.id,
      name: value.name,
      generated: value.generated,
      index: value.index,
      value: value.value,
      mandatory: value.mandatory,
      entityType: value.entityType,
      hidden: value.hidden,
      onCreate: value.onCreate,
      type: value.type,
    });
    return cloneDeep(this);
  }

  removeToCustomField(id: string) {
    this.product.toCustomFields.delete(id);
    return cloneDeep(this);
  }

  withFromCustomField(value: CustomFieldValue | null, id?: string) {
    if (!value && !id) throw new Error('Either value or id must be provided');
    if (!value) return this.removeFromCustomField(id!);
    this.product.fromCustomFields.set(value.id, {
      id: value.id,
      name: value.name,
      generated: value.generated,
      index: value.index,
      value: value.value,
      mandatory: value.mandatory,
      entityType: value.entityType,
      hidden: value.hidden,
      onCreate: value.onCreate,
      type: value.type,
    });
    return cloneDeep(this);
  }

  removeFromCustomField(id: string) {
    this.product.toCustomFields.delete(id);
    return cloneDeep(this);
  }

  withoutToCustomField(customFieldId: string) {
    this.product.toCustomFields.delete(customFieldId);
    return cloneDeep(this);
  }

  withoutFromCustomField(customFieldId: string) {
    this.product.fromCustomFields.delete(customFieldId);
    return cloneDeep(this);
  }

  withoutCustomField(customFieldId: string) {
    this.customFields.delete(customFieldId);
    return cloneDeep(this);
  }

  withContainerId(containerId?: string) {
    this.product.containerId = containerId;
    return cloneDeep(this);
  }

  withProcessType(type: ProductTransactionProcessType) {
    this.processType = type;
    return cloneDeep(this);
  }

  withKitComplete(complete: boolean) {
    if (!this.product.kit) this.product.kit = new KitSpecifiers();
    this.product.kit.complete = complete;
    return cloneDeep(this);
  }

  withKitTemplateId(templateId?: string) {
    if (!this.product.kit) this.product.kit = new KitSpecifiers();
    this.product.kit.templateId = templateId;
    return cloneDeep(this);
  }

  withFromKitId(kitId?: string) {
    if (!this.product.kit) this.product.kit = new KitSpecifiers();
    this.product.kit.fromKitId = kitId;
    return cloneDeep(this);
  }

  withToKitId(kitId?: string) {
    if (!this.product.kit) this.product.kit = new KitSpecifiers();
    this.product.kit.toKitId = kitId;
    return cloneDeep(this);
  }
}

export interface ProductTransaction extends WithCustomFields {}
applyMixins(ProductTransaction, [WithCustomFields]);

export class CreateProductTransactionInput extends forCreate(ProductTransaction) {}

export class UpdateProductTransactionInput extends forUpdate(ProductTransaction) {}

export class DeleteProductTransactionInput extends forDelete(ProductTransaction) {}
